import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { IRadioButton } from "@app/directives/_forms/_radio-buttons/radio-button-base/radio-button-base.component";
import { CameraLocation } from "@app/main/components/videos/enums/CameraLocation";
import { IVideoStream } from "@app/main/components/videos/models/ICameraChannel";
import { IVehicle } from "@interfaces/IVehicle";
import { TranslationService } from "@services/translation.service";
import { CameraChannelType } from "../enums/CameraChannelType";
import { CameraStatusService } from "../services/camera-status.service";
import { CameraService } from "../services/camera.service";
import "./video-channel-selector.component.scss";

@Component({
    selector: 'video-channel-selector',
    template: require('./video-channel-selector.component.html')
})
export class VideoChannelSelectorComponent implements OnInit, OnChanges {
    @Input() isPreview: boolean = false;
    @Input() vehicle: IVehicle;
    @Input() availableChannels: IVideoStream[];
    @Input() autoSelectAll = false;
    @Input() dropBottom: boolean;

    @Output() channelsChange: EventEmitter<IVideoStream[]> = new EventEmitter<IVideoStream[]>();

    channelSelectorLabel = "";
    isOpen = false;

    loading = false;
    channels: IVideoStream[] = [];
    visibleChannels: IVideoStream[] = [];
    channelRadioButtons: IRadioButton[];

    CameraLocation = CameraLocation;
    CameraChannelType = CameraChannelType;

    constructor(
        private readonly translationService: TranslationService,
        private readonly cameraService: CameraService,
        private readonly cameraStatusService: CameraStatusService) {

    }

    ngOnInit(): void {
        this.updateChannelSelectorLabel();
    }

    ngOnChanges(): void {
        if (this.availableChannels) {
            this.processChannels(this.availableChannels);
        } else {
            this.getAllActiveStreamingChannels();
        }
    }

    getAllActiveStreamingChannels(): void {
        this.cameraStatusService.getVideoChannels(this.vehicle.Id).then(channels => {
            this.processChannels(channels);
        });
    }

    private processChannels(channels: IVideoStream[] = []): void {
        this.channels = this.cameraService.filterDuplicateVideoStreams(channels).filter(c => c.IsActive);

        this.setSelectedChannels();
        this.updateChannelSelectorLabel();
        this.createChannelCards(this.channels);
    }

    setSelectedChannels() {
        if (this.autoSelectAll) {
            this.channels.forEach(channel => { channel.visible = true; });
        }
        else {
            const primary = this.cameraService.getPrimaryChannel(this.channels);
            if (primary) {
                primary.visible = true;
            }
        }

        this.updateVisibleChannelList();
    }

    toggleChannelVisibility(channel: IVideoStream) {
        if (!channel) {
            return;
        }

        channel.visible = !channel.visible;

        if (channel.visible && !this.isPreview) {
            this.cameraService.getLiveStreamUrls(this.vehicle.Id, null).then(channels => {
                channel.StreamUrl = this.cameraService.filterDuplicateVideoStreams(channels).find(c => c.ChannelId == channel.ChannelId)?.StreamUrl;

                this.updateVisibleChannelList();
            });
        }
        else {
            this.updateVisibleChannelList();
        }
    }

    togglePopup() {
        this.isOpen = !this.isOpen;
    }

    onRadioButtonSelectionChanged(selectedButtons: IRadioButton[]): void {
        this.updateVisibleChannelList();
    }

    // ---

    private createChannelCards(channels: IVideoStream[]): void {
        if (this.channelRadioButtons?.length) {
            return;
        }

        this.channels = channels;
        this.channelRadioButtons = [];

        if (!this.channels?.length) {
            return;
        }

        // Sort
        this.channels.sort((a, b) => {
            if (a.CameraLocation == CameraLocation.Forward) {
                return -1;
            }

            return a.ChannelId - b.ChannelId;;
        });

        this.channels.forEach(channel => {
            const channelLabel: string = this.translationService.tEnum(CameraLocation, "CameraLocation", channel.CameraLocation);

            this.channelRadioButtons.push({
                label: `${channelLabel}`,
                description: `Channel ${channel.ChannelId}`,
                value: channel,
                selected: channel.visible
            });
        });

        this.updateVisibleChannelList();
    }

    private updateVisibleChannelList() {
        this.visibleChannels = (this.channelRadioButtons?.length)
            ? this.channelRadioButtons.filter((button: IRadioButton) => button.selected).map(button => button.value)
            : [];

        this.updateChannelSelectorLabel();

        this.channelsChange.emit(this.visibleChannels);
    }

    private updateChannelSelectorLabel() {

        if (this.visibleChannels.length == 0) {
            this.channelSelectorLabel = this.translationService.t('videoChannelSelector.noChannelsSelected');
            return;
        }

        this.channelSelectorLabel = "";
        for (let i = 0; i < Math.min(this.visibleChannels.length, 3); i++) {
            if (i > 0) {
                this.channelSelectorLabel += ", ";
            }

            this.channelSelectorLabel += this.translationService.tEnum(CameraLocation, "CameraLocation", this.visibleChannels[i].CameraLocation);
        }

        if (this.visibleChannels.length > 3) {
            this.channelSelectorLabel += ` (+${this.visibleChannels.length - 3})`;
        }
    }
}

